import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  BarChart3, RefreshCcw, TrendingUp, Building2, Users, 
  GraduationCap, BookOpen, FileText, Video, Ticket, Edit
} from 'lucide-react';

const AnalyticsPage = ({ onMenuClick }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/super-admin/analytics?period=${period}`);
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const growth = data?.growth || {};
  const activity = data?.activity || {};
  const totals = data?.totals || {};
  const distribution = data?.distribution || {};

  const growthCards = [
    { label: 'Nouveaux centres', value: growth.new_centers, icon: Building2, color: 'text-violet-600' },
    { label: 'Nouveaux étudiants', value: growth.new_students, icon: GraduationCap, color: 'text-blue-600' },
    { label: 'Nouveaux formateurs', value: growth.new_trainers, icon: Users, color: 'text-emerald-600' },
    { label: 'Nouveaux cours', value: growth.new_courses, icon: BookOpen, color: 'text-pink-600' },
  ];

  const activityCards = [
    { label: 'Documents générés', value: activity.documents_generated, icon: FileText },
    { label: 'Sessions live', value: activity.live_sessions, icon: Video },
    { label: 'Émargements signés', value: activity.attendance_signatures, icon: Edit },
    { label: 'Tickets créés', value: activity.tickets_created, icon: Ticket },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Analytics Globales
            </h1>
            <p className="text-slate-500">Vue d'ensemble de la plateforme</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">7 derniers jours</SelectItem>
              <SelectItem value="month">30 derniers jours</SelectItem>
              <SelectItem value="quarter">90 derniers jours</SelectItem>
              <SelectItem value="year">12 derniers mois</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-violet-600">{totals.centers}</p>
            <p className="text-sm text-slate-500">Centres</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{totals.students}</p>
            <p className="text-sm text-slate-500">Étudiants</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">{totals.trainers}</p>
            <p className="text-sm text-slate-500">Formateurs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-pink-600">{totals.courses}</p>
            <p className="text-sm text-slate-500">Cours</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-cyan-600">{totals.groups}</p>
            <p className="text-sm text-slate-500">Groupes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{totals.documents}</p>
            <p className="text-sm text-slate-500">Documents</p>
          </CardContent>
        </Card>
      </div>

      {/* Growth */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Croissance sur la période
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {growthCards.map((card, idx) => (
              <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                  <span className="text-sm text-slate-500">{card.label}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  +{card.value || 0}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Activité
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {activityCards.map((card, idx) => (
              <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <card.icon className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {card.value || 0}
                </p>
                <p className="text-sm text-slate-500">{card.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Distribution par plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(distribution.by_plan || {}).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <span className="font-medium">{plan || 'Sans plan'}</span>
                  <Badge variant="secondary">{count} centre(s)</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribution par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(distribution.by_status || {}).map(([status, count]) => {
                const statusLabels = {
                  active: 'Actif',
                  suspended: 'Suspendu',
                  trial: 'Essai',
                  expired: 'Expiré',
                  none: 'Non défini'
                };
                const statusColors = {
                  active: 'bg-emerald-100 text-emerald-700',
                  suspended: 'bg-red-100 text-red-700',
                  trial: 'bg-blue-100 text-blue-700',
                  expired: 'bg-slate-100 text-slate-700'
                };
                return (
                  <div key={status} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Badge className={statusColors[status] || 'bg-slate-100'}>
                      {statusLabels[status] || status}
                    </Badge>
                    <span className="font-bold">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
